import React from 'react';
import ProductControls from './ProductControls';
import OptimizedPicture from './OptimizedPicture';
import { getProductImageSources } from '../utils/productImageUtils';
import { formatPricePerUnit, isBulkCatalogProduct } from '../../../utils/bulkSaleUtils';

const formatPrice = (price) => {
  if (price == null || isNaN(price)) return '';
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(price);
};

const isPromotionActive = (promotion) => {
  if (!promotion) return false;
  if (promotion.deletedAt) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (promotion.startDate) {
    const start = new Date(promotion.startDate);
    start.setHours(0, 0, 0, 0);
    if (start > today) return false;
  }
  if (promotion.endDate) {
    const end = new Date(promotion.endDate);
    end.setHours(23, 59, 59, 999);
    if (end < today) return false;
  }
  return true;
};

const formatPromotionBadge = (promotion) => {
  if (!promotion) return '';
  if (promotion.promotionType === 'QUANTITY_PROMOTION') {
    return `${promotion.buyQuantity}x${promotion.payQuantity}`;
  }
  if (promotion.promotionType === 'PERCENTAGE_DISCOUNT') {
    return `-${promotion.discountPercentage}%`;
  }
  return 'PROMO';
};

const calculateDiscountedPrice = (price, discountPercentage) => {
  const discount = (Number(price) * Number(discountPercentage)) / 100;
  return Number(price) - discount;
};

const ProductCard = React.memo(({
  product,
  branchConfig,
  onAdd,
  onMinus,
  onProductClick,
  count,
  isLast = false,
}) => {
  const {
    hasBase64Image,
    optimizedImageUrls,
    imageUrl,
    hasImage,
    hasOptimizedImage,
  } = getProductImageSources(product);

  const hasBrand = product.brandName != null && product.brandName !== '';
  const isBulk = isBulkCatalogProduct(product);
  const showPrice = branchConfig?.priceActive;
  const showControls = branchConfig?.status;

  const hasActivePromotion = product.activePromotion && isPromotionActive(product.activePromotion);
  const isPercentageDiscount = hasActivePromotion && product.activePromotion.promotionType === 'PERCENTAGE_DISCOUNT';
  const discountedPrice = isPercentageDiscount
    ? calculateDiscountedPrice(product.price, product.activePromotion.discountPercentage)
    : null;

  const renderImage = () => {
    if (!hasImage) {
      return null;
    }

    if (hasBase64Image) {
      return (
        <img
          src={imageUrl}
          alt={product.name}
          loading="lazy"
          decoding="async"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    }

    if (hasOptimizedImage && optimizedImageUrls) {
      return (
        <OptimizedPicture
          srcJpg={optimizedImageUrls.jpg || imageUrl}
          srcWebp={optimizedImageUrls.webp}
          srcAvif={optimizedImageUrls.avif}
          alt={product.name}
          loading="lazy"
          decoding="async"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    }

    return (
      <img
        src={imageUrl}
        alt={product.name}
        loading="lazy"
        decoding="async"
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    );
  };

  return (
    <div className={`carta-v7-product-row${isLast ? ' carta-v7-product-row--last' : ''}${!hasImage ? ' carta-v7-product-row--no-image' : ''}${hasActivePromotion ? ' carta-v7-product-row--has-promo' : ''}`}>
      <button
        type="button"
        className="carta-v7-product-row__main"
        onClick={() => onProductClick?.(product)}
        aria-label={`Ver detalle de ${product.name}`}
      >
        {hasImage && (
          <div className="carta-v7-product-row__image">
            {renderImage()}
          </div>
        )}

        <div className="carta-v7-product-row__body">
          {(hasActivePromotion || hasBrand || isBulk) && (
            <div className="carta-v7-product-row__meta">
              {hasActivePromotion && (
                <span className="carta-v7-product-row__badge carta-v7-product-row__badge--promo">
                  <span className="carta-v7-product-row__badge-icon" aria-hidden="true">🔥</span>
                  {formatPromotionBadge(product.activePromotion)}
                </span>
              )}
              {isBulk && (
                <span className="carta-v7-product-row__badge carta-v7-product-row__badge--bulk">
                  Granel
                </span>
              )}
              {hasBrand && (
                <span className="carta-v7-product-row__brand">{product.brandName}</span>
              )}
            </div>
          )}
          <div className="carta-v7-product-row__name">{product.name}</div>
          {product.description && (
            <div className="carta-v7-product-row__desc">{product.description}</div>
          )}
          {showPrice && (
            isPercentageDiscount ? (
              <div className="carta-v7-product-row__price-promo">
                <span className="carta-v7-product-row__price carta-v7-product-row__price--before">
                  {formatPrice(product.price)}
                </span>
                <span className="carta-v7-product-row__price-promo-highlight">
                  <span className="carta-v7-product-row__price carta-v7-product-row__price--discounted">
                    {formatPrice(discountedPrice)}
                  </span>
                  <span className="carta-v7-product-row__price--savings">
                    Ahorrás {formatPrice(product.price - discountedPrice)}
                  </span>
                </span>
              </div>
            ) : (
              <div className="carta-v7-product-row__price">
                {isBulk
                  ? formatPricePerUnit(product.price, product.measurementUnit)
                  : formatPrice(product.price)}
              </div>
            )
          )}
        </div>
      </button>

      {showPrice && showControls && (
        <div className="carta-v7-product-row__controls">
          <ProductControls
            productId={product.id}
            count={count}
            onAdd={onAdd}
            onMinus={onMinus}
          />
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => (
  prevProps.product.id === nextProps.product.id &&
  prevProps.count === nextProps.count &&
  prevProps.isLast === nextProps.isLast &&
  prevProps.onProductClick === nextProps.onProductClick &&
  prevProps.branchConfig?.status === nextProps.branchConfig?.status &&
  prevProps.branchConfig?.priceActive === nextProps.branchConfig?.priceActive
));

ProductCard.displayName = 'ProductCard';

export default ProductCard;
